// client/src/components/company/VatCreditRequestDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { formatInterestRate, formatCurrencyInput, parseCurrencyInput, formatCurrencyWithSymbol } from '../utils/formatUtils';
import { Info } from 'lucide-react';
import VatCreditAttachmentList from '../vatCredit/VatCreditAttachmentList';
import '../../styles/RequestDetail.css';
import '../../styles/VatCreditRequestDetail.css';

const VatCreditRequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [request, setRequest] = useState(null);
  const [bids, setBids] = useState([]);
  const [affiliatedBanks, setAffiliatedBanks] = useState([]);
  const [thirdPartyBanks, setThirdPartyBanks] = useState([]);
  const [banksLoaded, setBanksLoaded] = useState(false);
  const [allocations, setAllocations] = useState({});
  const [totalAllocated, setTotalAllocated] = useState(0);
  const [allocationError, setAllocationError] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processingAllocation, setProcessingAllocation] = useState(false);
  const [processingAction, setProcessingAction] = useState(false);
  const [allocationCompleted, setAllocationCompleted] = useState(false);

  // Track if we're currently editing an allocation input
  const [editingInputId, setEditingInputId] = useState(null);
  const [editingInputValue, setEditingInputValue] = useState('');

  // Helper function to get the correct bid ID
  const getBidId = (bid) => {
    return bid?._id || bid?.id;
  };

  // Helper function to check if there are any active bids
  const hasActiveBids = (bids) => {
    return bids.some(bid => bid.status === 'pending');
  };

  // Helper function to translate transfer notification type
  const getTransferNotificationTranslation = (transferNotification) => {
    return transferNotification === 'notified'
      ? t('vatCredit.notified')
      : t('vatCredit.notNotified');
  };

  // Risolve i nomi delle banche selezionate, come nelle richieste di allocazione (RequestDetail.js)
  const getSelectedBankNames = () => {
    if (!request || !request.selectedBanks || request.selectedBanks.length === 0 || !banksLoaded) {
      return [];
    }

    const bankNames = request.selectedBanks.map(selectedBankId => {
      const affiliatedBank = affiliatedBanks.find(ab =>
        ab.userId?.toString() === selectedBankId?.toString() ||
        ab._id?.toString() === selectedBankId?.toString()
      );

      if (affiliatedBank) {
        if (affiliatedBank.organizationId && affiliatedBank.organizationId.name) {
          return affiliatedBank.organizationId.name;
        }
        if (affiliatedBank.referralDetails && affiliatedBank.referralDetails.bankName) {
          return affiliatedBank.referralDetails.bankName;
        }
        if (affiliatedBank.bankName) {
          return affiliatedBank.bankName;
        }
        if (affiliatedBank.firstName && affiliatedBank.lastName) {
          return `${affiliatedBank.firstName} ${affiliatedBank.lastName}`;
        }
      }

      const thirdPartyBank = thirdPartyBanks.find(tp =>
        tp._id?.toString() === selectedBankId?.toString()
      );

      if (thirdPartyBank) {
        if (thirdPartyBank.organization?.name) {
          return thirdPartyBank.organization.name;
        }
        if (thirdPartyBank.firstName && thirdPartyBank.lastName) {
          return `${thirdPartyBank.firstName} ${thirdPartyBank.lastName}`;
        }
      }

      return null;
    });

    return [...new Set(bankNames.filter(Boolean))];
  };

  // Helper function to get localized server error messages
  const getLocalizedServerError = (serverMessage) => {
    if (typeof serverMessage !== 'string') return serverMessage;

    const lowerMessage = serverMessage.toLowerCase();

    if (i18n.language === 'it') {
      if (lowerMessage.includes('total allocation exceeds')) {
        return 'L\'allocazione totale supera l\'importo della richiesta';
      }
      if (lowerMessage.includes('exceeds the bid')) {
        return 'L\'allocazione supera l\'ammontare desiderato dall\'offerta';
      }
      if (lowerMessage.includes('not in the pending allocation state')) {
        return 'La richiesta non è in stato di allocazione in attesa';
      }
      if (lowerMessage.includes('not found')) {
        return 'Risorsa non trovata';
      }
    }

    return serverMessage;
  };

  useEffect(() => {
    const fetchRequestDetails = async () => {
      try {
        const res = await axios.get(`/api/vat-credit-requests/${id}`);

        setRequest(res.data.request);

        // Ordine crescente per tasso: il tasso più basso è il migliore per l'azienda
        const sortedBids = [...(res.data.bids || [])].sort((a, b) => a.interestRate - b.interestRate);
        setBids(sortedBids);

        // Initialize allocations for pending allocation requests
        if (res.data.request.status === 'pending_allocation') {
          const initialAllocations = {};

          sortedBids.forEach(bid => {
            const bidId = getBidId(bid);

            if (!bidId) {
              console.error('Invalid bid object (no ID found)');
              return;
            }

            initialAllocations[bidId] = {
              amount: 0
            };
          });

          setAllocations(initialAllocations);
          setTotalAllocated(0);
        }

        setLoading(false);
      } catch (err) {
        setError(t('common.errors.fetchDetails'));
        setLoading(false);
      }
    };

    fetchRequestDetails();
  }, [id, t]);

  // Fetch delle banche (relazione + terze) per risolvere selectedBanks in nomi, come RequestDetail.js
  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const referralsRes = await axios.get('/api/company/bank-referrals');
        setAffiliatedBanks(referralsRes.data.bankReferrals || []);

        const thirdPartyRes = await axios.get('/api/requests/third-party-banks');
        setThirdPartyBanks(thirdPartyRes.data.thirdPartyBanks || []);
      } catch (err) {
        setAffiliatedBanks([]);
        setThirdPartyBanks([]);
      } finally {
        setBanksLoaded(true);
      }
    };

    fetchBanks();
  }, []);

  const handleAllocationChange = (bidId, value) => {
    if (!allocations[bidId]) {
      console.error('Tried to update allocation for non-existent bid');
      return;
    }

    // Keep the formatted string for display during editing
    const rawValue = value;
    let formatted;
    if (rawValue.endsWith('.') || rawValue.endsWith(',')) {
      formatted = formatCurrencyInput(rawValue.slice(0, -1));
      formatted = formatted ? formatted + '.' : '';
    } else {
      formatted = formatCurrencyInput(rawValue);
    }
    setEditingInputValue(formatted);

    // Parse the numeric value for calculations
    const amount = parseCurrencyInput(value);

    const updatedAllocations = {
      ...allocations,
      [bidId]: {
        ...allocations[bidId],
        amount
      }
    };

    setAllocations(updatedAllocations);

    const total = Object.values(updatedAllocations).reduce(
      (sum, alloc) => sum + alloc.amount,
      0
    );

    setTotalAllocated(total);

    if (total > request.amount) {
      setAllocationError(t('vatCredit.errors.exceedsRequestAmount'));
    } else {
      setAllocationError('');
    }
  };

  const handleInputFocus = (bidId) => {
    setEditingInputId(bidId);
    if (allocations[bidId]) {
      setEditingInputValue(formatCurrencyInput(allocations[bidId].amount));
    }
  };

  const handleInputBlur = () => {
    setEditingInputId(null);
  };

  // Helper to prepare bid allocations array
  const prepareBidAllocations = () => {
    const bidAllocations = [];

    for (const bid of bids) {
      if (!bid) continue;

      const bidId = getBidId(bid);
      if (!bidId) continue;

      const allocation = allocations[bidId];
      if (!allocation) continue;

      if (bid.status !== 'pending') continue;

      if (allocation.amount > 0) {
        bidAllocations.push({
          bidId: bidId,
          amount: allocation.amount
        });
      }
    }

    return bidAllocations;
  };

  const confirmAllocation = async () => {
    if (totalAllocated > request.amount) {
      return setAllocationError(t('vatCredit.errors.exceedsRequestAmount'));
    }

    const hasBidsWithAllocation = Object.values(allocations).some(allocation =>
      allocation.amount > 0
    );

    if (!hasBidsWithAllocation) {
      return setAllocationError(t('allocation.errors.noAllocation'));
    }

    for (const bid of bids) {
      if (!bid || bid.status !== 'pending') continue;

      const bidId = getBidId(bid);
      if (!bidId || !allocations[bidId]) continue;

      const allocation = allocations[bidId];
      if (allocation.amount > bid.desiredAmount) {
        return setAllocationError(
          i18n.language === 'it'
            ? `L'allocazione per ${bid.bank?.name} (${formatCurrencyWithSymbol(allocation.amount, request.currency || 'EUR')}) supera l'ammontare desiderato (${formatCurrencyWithSymbol(bid.desiredAmount, request.currency || 'EUR')})`
            : `Allocation for ${bid.bank?.name} (${formatCurrencyWithSymbol(allocation.amount, request.currency || 'EUR')}) exceeds their desired amount (${formatCurrencyWithSymbol(bid.desiredAmount, request.currency || 'EUR')})`
        );
      }
    }

    setAllocationError('');

    await executeAllocation();
  };

  const executeAllocation = async () => {
    try {
      setProcessingAllocation(true);
      setError('');
      setAllocationError('');

      const bidAllocations = prepareBidAllocations();

      if (bidAllocations.length === 0) {
        setProcessingAllocation(false);
        return setAllocationError(t('allocation.errors.noActiveBids'));
      }

      await axios.post('/api/vat-credit-bids/allocate', {
        requestId: request.id || request._id,
        allocations: bidAllocations
      });

      setAllocationCompleted(true);

      setTimeout(() => {
        navigate('/company/vat-credit/past-requests', { replace: true });
      }, 2500);

    } catch (err) {
      setProcessingAllocation(false);

      if (err.response?.status === 400 && err.response?.data?.message) {
        setAllocationError(getLocalizedServerError(err.response.data.message));
      } else if (err.response?.status === 404) {
        setError(i18n.language === 'it' ? 'Risorsa non trovata' : 'Resource not found');
      } else {
        setError(i18n.language === 'it'
          ? 'Impossibile confermare l\'allocazione. Contatta l\'amministratore.'
          : 'Unable to confirm the allocation. Contact the administrator.');
      }
    }
  };

  const updateRequestStatus = async (newStatus) => {
    if (newStatus === 'cancelled') {
      const confirmMessage = i18n.language === 'it'
        ? 'Sei sicuro di voler annullare questa richiesta? Questa azione non può essere annullata.'
        : 'Are you sure you want to cancel this request? This action cannot be undone.';

      if (!window.confirm(confirmMessage)) {
        return;
      }
    }

    try {
      setProcessingAction(true);
      setError('');

      await axios.put(`/api/vat-credit-requests/${id}/status`, { status: newStatus });

      if (newStatus === 'cancelled') {
        navigate('/company/vat-credit/past-requests');
      } else {
        const res = await axios.get(`/api/vat-credit-requests/${id}`);
        setRequest(res.data.request);

        const sortedBids = [...(res.data.bids || [])].sort((a, b) => a.interestRate - b.interestRate);
        setBids(sortedBids);

        if (newStatus === 'pending_allocation') {
          const initialAllocations = {};

          sortedBids.forEach(bid => {
            const bidId = getBidId(bid);

            if (!bidId) {
              console.error('Invalid bid object (no ID found)');
              return;
            }

            initialAllocations[bidId] = {
              amount: 0
            };
          });

          setAllocations(initialAllocations);
          setTotalAllocated(0);
        }
      }

      setProcessingAction(false);
    } catch (err) {
      console.error('Error updating VAT credit request status:');

      let errorMessage = t('common.errors.updateStatus');
      if (err.response?.data?.message) {
        errorMessage = getLocalizedServerError(err.response.data.message);
      }

      setError(errorMessage);
      setProcessingAction(false);
    }
  };

  // Render bids come mobile cards
  const renderMobileBidCard = (bid) => {
    const bidId = getBidId(bid);
    if (!bid || !bidId) return null;

    const isCancelled = bid.status === 'cancelled';

    return (
      <div key={bidId} className={`mobile-record-card ${isCancelled ? 'cancelled' : ''}`}>
        <div className="mobile-card-header">
          <div className="mobile-card-title">
            {bid.bank?.name || t('bids.notAvailable')}
          </div>
          <div className="mobile-card-status">
            <span className={`status status-${bid.status}`}>
              {t(`status.${bid.status}`)}
            </span>
          </div>
        </div>

        <div className="mobile-card-body">
          <div className="mobile-card-row">
            <span className="mobile-card-label">{t('bids.interestRate')}</span>
            <span className="mobile-card-value">
              <span className="interest-rate">
                {formatInterestRate(bid.interestRate, bid.rateType, bid.baseRate)}
              </span>
            </span>
          </div>

          <div className="mobile-card-row">
            <span className="mobile-card-label">{t('requests.rateType')}</span>
            <span className="mobile-card-value">
              <span className={`rate-type-badge ${bid.rateType}`}>
                {bid.rateType === 'fixed' ? t('requests.fixed') : t('requests.floating')}
              </span>
            </span>
          </div>

          <div className="mobile-card-row">
            <span className="mobile-card-label">{t('vatCredit.bankIndicativeDays')}</span>
            <span className="mobile-card-value">{bid.indicativeDays} {t('common.days')}</span>
          </div>

          <div className="mobile-card-row">
            <span className="mobile-card-label">{t('vatCredit.commission')}</span>
            <span className="mobile-card-value">
              {bid.commission !== undefined && bid.commission !== null
                ? `${bid.commission}%`
                : t('bids.notAvailable')}
            </span>
          </div>

          <div className="mobile-card-row">
            <span className="mobile-card-label">{t('vatCredit.transferNotification')}</span>
            <span className="mobile-card-value">
              {bid.transferNotification
                ? getTransferNotificationTranslation(bid.transferNotification)
                : t('bids.notAvailable')}
            </span>
          </div>

          <div className="mobile-card-row">
            <span className="mobile-card-label">{t('vatCredit.desiredAmount')}</span>
            <span className="mobile-card-value">
              {formatCurrencyWithSymbol(bid.desiredAmount, request.currency || 'EUR')}
            </span>
          </div>

          {request.status === 'pending_allocation' && allocations[bidId] && (
            <div className="mobile-card-row" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
              <span className="mobile-card-label" style={{ marginBottom: '8px' }}>
                {t('allocation.yourAllocation')}
              </span>
              <input
                type="text"
                className="mobile-allocation-input"
                value={editingInputId === bidId
                  ? editingInputValue
                  : formatCurrencyWithSymbol(allocations[bidId].amount, request.currency || 'EUR')}
                onChange={(e) => !isCancelled && handleAllocationChange(bidId, e.target.value)}
                onFocus={() => !isCancelled && handleInputFocus(bidId)}
                onBlur={handleInputBlur}
                disabled={processingAllocation || isCancelled}
                placeholder={t('allocation.enterAmount')}
              />
            </div>
          )}
        </div>

        {bid.notes && (
          <div className="mobile-bid-extra">
            <div className="detail-item-modern">
              <div className="detail-label">{t('requestDetail.bidDetails.bankNotes')}</div>
              <div className="detail-value">{bid.notes}</div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="loading">{t('common.loadingRequests')}</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (!request) {
    const notFoundMessage = i18n.language === 'it' ? 'Richiesta non trovata' : 'Request not found';
    return <div className="error-message">{notFoundMessage}</div>;
  }

  if (allocationCompleted) {
    return (
      <div className="card">
        <div className="alert-success" style={{ padding: '20px', margin: '20px 0' }}>
          <h3>{t('requestDetail.messages.allocationCompletedSuccess')}</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="request-detail company-dashboard">
      <div className="page-navigation" style={{ marginBottom: '14px' }}>
        <button
          type="button"
          className="back-btn"
          onClick={() => navigate('/company/vat-credit/past-requests')}
          disabled={processingAction}
        >
          {t('requestDetail.navigation.backToRequests')}
        </button>
      </div>

      <div className="card">
        <div className="card-title">
          <span>
            <Info className="card-title-icon" size={20} />
            {t('vatCredit.requestDetails')}
          </span>
          <div>
            <span className={`status status-${request.status}`}>
              {t(`status.${request.status}`)}
            </span>
          </div>
        </div>

        <div className="request-info">
          <h3>{t('vatCredit.requestInformation')}</h3>

          <div className="info-grid">
            <div className="info-card">
              <div className="info-card-label">{t('requests.requestId')}</div>
              <div className="info-card-value">#{request.requestId}</div>
            </div>

            <div className="info-card currency">
              <div className="info-card-label">{t('requests.currency')}</div>
              <div className="info-card-value">{request.currency || 'EUR'}</div>
            </div>

            <div className="info-card amount">
              <div className="info-card-label">{t('vatCredit.amount')}</div>
              <div className="info-card-value">{formatCurrencyWithSymbol(request.amount, request.currency || 'EUR')}</div>
            </div>

            <div className="info-card">
              <div className="info-card-label">{t('vatCredit.indicativeDays')}</div>
              <div className="info-card-value">{request.indicativeDays} {t('common.days')}</div>
            </div>

            <div className="info-card">
              <div className="info-card-label">{t('vatCredit.bidDeadline')}</div>
              <div className="info-card-value">
                {new Date(request.bidDeadline).toLocaleDateString()}
                {' '}
                <span style={{ color: '#6c757d', fontSize: '0.9em' }}>
                  {new Date(request.bidDeadline).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} CET
                </span>
              </div>
            </div>

            <div className="info-card">
              <div className="info-card-label">{t('vatCredit.executionDate')}</div>
              <div className="info-card-value">{new Date(request.executionDate).toLocaleDateString()}</div>
            </div>

            <div className="info-card">
              <div className="info-card-label">{t('requests.dateCreated')}</div>
              <div className="info-card-value">{new Date(request.createdAt).toLocaleDateString()}</div>
            </div>

            {request.selectedBanks && request.selectedBanks.length > 0 && (
              <div className="info-card">
                <div className="info-card-label">{t('requests.selectedBanks')}</div>
                <div className="info-card-value">
                  {(() => {
                    const bankNames = getSelectedBankNames();
                    return bankNames.length > 0
                      ? bankNames.join(', ')
                      : `${request.selectedBanks.length} ${t('requests.banksSelected')}`;
                  })()}
                </div>
              </div>
            )}

            <div className="info-card">
              <div className="info-card-label">{t('requests.createdBy')}</div>
              <div className="info-card-value">
                {request.createdBy ? `${request.createdBy.firstName} ${request.createdBy.lastName}` : t('bids.notAvailable')}
              </div>
            </div>

            {request.notes && (
              <div className="info-card notes-section">
                <div className="info-card-label">{t('requestDetail.info.notes')}</div>
                <div className="info-card-value">{request.notes}</div>
              </div>
            )}
          </div>
        </div>

        <div className="request-info">
          <VatCreditAttachmentList
            requestId={request.id || request._id}
            attachments={request.attachments}
          />
        </div>

        {request.status === 'pending_allocation' && (
          <div className="allocation-summary">
            <div className="allocation-summary-header">{t('allocation.summary')}</div>
            <div className="allocation-summary-item">
              <span>{t('vatCredit.amount')}:</span>
              <span>{formatCurrencyWithSymbol(request.amount, request.currency || 'EUR')}</span>
            </div>
            <div className="allocation-summary-item">
              <span>{t('allocation.totalAllocated')}:</span>
              <span>{formatCurrencyWithSymbol(totalAllocated, request.currency || 'EUR')}</span>
            </div>
            {allocationError && (
              <div className="allocation-error">{allocationError}</div>
            )}
            <div className="allocation-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirmAllocation}
                disabled={!!allocationError || processingAllocation}
              >
                {processingAllocation ? t('allocation.processing') : t('allocation.confirmAllocation')}
              </button>
            </div>
          </div>
        )}

        <div className="bids-section">
          <h3>
            {request.status === 'pending_allocation'
              ? t('requestDetail.bidsSection.allocationManagement')
              : request.status === 'completed'
              ? t('requestDetail.bidsSection.finalAllocation')
              : request.status === 'cancelled'
              ? t('requestDetail.bidsSection.cancelledRequest')
              : t('requestDetail.bidsSection.bidsReceived')}
          </h3>

          {bids.length > 0 ? (
            <>
              {/* Desktop Table View */}
              <div className="bids-table-container">
                <table className="bids-table">
                  <thead>
                    <tr>
                      <th>{t('bids.bank')}</th>
                      <th>{t('bids.interestRate')}</th>
                      <th>{t('requests.rateType')}</th>
                      <th>{t('vatCredit.commission')}</th>
                      <th>{t('vatCredit.bankIndicativeDays')}</th>
                      <th>{t('vatCredit.transferNotification')}</th>
                      <th>{t('vatCredit.desiredAmount')}</th>
                      {request.status === 'pending_allocation' && <th>{t('allocation.yourAllocation')}</th>}
                      <th>{t('bids.status')}</th>
                      <th>{t('bids.submitted')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bids.map(bid => {
                      const bidId = getBidId(bid);

                      if (!bid || !bidId) {
                        console.error('Invalid bid object in render');
                        return null;
                      }

                      const isCancelled = bid.status === 'cancelled';

                      return (
                        <React.Fragment key={bidId}>
                          <tr className={isCancelled ? 'cancelled-bid' : ''}>
                            <td>
                              <div className="bank-name">
                                {bid.bank?.name || t('bids.notAvailable')}
                              </div>
                            </td>
                            <td>
                              <div className="interest-rate">
                                {formatInterestRate(bid.interestRate, bid.rateType, bid.baseRate)}
                              </div>
                            </td>
                            <td>
                              <span className={`rate-type-badge ${bid.rateType}`}>
                                {bid.rateType === 'fixed' ? t('requests.fixed') : t('requests.floating')}
                              </span>
                            </td>
                            <td>
                              {bid.commission !== undefined && bid.commission !== null
                                ? `${bid.commission}%`
                                : t('bids.notAvailable')}
                            </td>
                            <td>{bid.indicativeDays}</td>
                            <td>
                              {bid.transferNotification
                                ? getTransferNotificationTranslation(bid.transferNotification)
                                : t('bids.notAvailable')}
                            </td>
                            <td>
                              <div className="amount-display primary">
                                {formatCurrencyWithSymbol(bid.desiredAmount, request.currency || 'EUR')}
                              </div>
                            </td>
                            {request.status === 'pending_allocation' && allocations[bidId] && (
                              <td>
                                <div className="allocation-input-container">
                                  <input
                                    type="text"
                                    className={`allocation-input ${isCancelled ? 'disabled' : ''}`}
                                    value={editingInputId === bidId
                                      ? editingInputValue
                                      : formatCurrencyWithSymbol(allocations[bidId].amount, request.currency || 'EUR')}
                                    onChange={(e) => !isCancelled && handleAllocationChange(bidId, e.target.value)}
                                    onFocus={() => !isCancelled && handleInputFocus(bidId)}
                                    onBlur={handleInputBlur}
                                    disabled={processingAllocation || isCancelled}
                                    placeholder={t('allocation.enterAmount')}
                                  />
                                  {isCancelled && (
                                    <div className="cancelled-bid-message">
                                      {t('requestDetail.messages.cancelledBidMessage')}
                                    </div>
                                  )}
                                </div>
                              </td>
                            )}
                            <td>
                              <span className={`status status-${bid.status}`}>
                                {t(`status.${bid.status}`)}
                              </span>
                            </td>
                            <td>
                              <div style={{ fontSize: '13px' }}>
                                {new Date(bid.createdAt).toLocaleDateString()}
                              </div>
                              <div style={{ fontSize: '11px', color: '#6c757d' }}>
                                {new Date(bid.createdAt).toLocaleTimeString()}
                              </div>
                            </td>
                          </tr>

                          {/* Additional details row */}
                          {(bid.notes || (request.status === 'completed' && bid.status === 'approved')) && (
                            <tr className="bid-details-row">
                              <td colSpan={request.status === 'pending_allocation' ? 10 : 9}>
                                <div className="bid-details-content">
                                  <div className="details-grid">
                                    {bid.notes && (
                                      <div className="detail-item-modern" style={{ gridColumn: '1 / -1' }}>
                                        <div className="detail-label">{t('requestDetail.bidDetails.bankNotes')}</div>
                                        <div className="detail-value">{bid.notes}</div>
                                      </div>
                                    )}

                                    {request.status === 'completed' && bid.status === 'approved' && (
                                      <div className="allocation-details-card" style={{ gridColumn: '1 / -1' }}>
                                        <h4>{t('bids.allocationDetails')}</h4>
                                        <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                          <div>
                                            <strong>{t('bids.allocatedAmount')}:</strong> {formatCurrencyWithSymbol(bid.allocatedAmount, request.currency || 'EUR')}
                                          </div>
                                          <div>
                                            <strong>{t('bids.interestRate')}:</strong> {formatInterestRate(bid.interestRate, bid.rateType, bid.baseRate)}
                                          </div>
                                          <div>
                                            <strong>{t('vatCredit.bankIndicativeDays')}:</strong> {bid.indicativeDays}
                                          </div>
                                          {bid.allocatedBy && (
                                            <div>
                                              <strong>{t('requests.allocatedBy')}:</strong> {bid.allocatedBy?.firstName} {bid.allocatedBy?.lastName}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards View */}
              <div className="mobile-cards-container">
                {bids.map(bid => renderMobileBidCard(bid))}
              </div>
            </>
          ) : (
            <div className="alert-info">
              <p>{t('requestDetail.bidsSection.noBidsYet')}</p>
            </div>
          )}
        </div>

        {request.status === 'completed' && (
          <div className="alert-success">
            <p>{t('requestDetail.messages.requestCompletedSuccess')}</p>
          </div>
        )}

        {request.status === 'cancelled' && (
          <div className="alert-danger">
            <p>{t('requestDetail.messages.requestCancelledInfo')}</p>
          </div>
        )}

        <div className="request-actions">
          {request.status === 'bidding' && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => updateRequestStatus('pending_allocation')}
              disabled={processingAction || !hasActiveBids(bids)}
              title={!hasActiveBids(bids) ? t('requestDetail.actions.atLeastOneBidRequired') : ''}
            >
              {processingAction ? t('actions.processing') : t('actions.closeBiddingProceed')}
            </button>
          )}

          {request.status === 'bidding' && (
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => updateRequestStatus('cancelled')}
              disabled={processingAction}
            >
              {processingAction ? t('actions.processing') : t('actions.cancelRequest')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VatCreditRequestDetail;
